import React, { useEffect, useRef, type JSX } from 'react';

interface ClickHandler {
    (e: Event): void;
}

interface OutsideClickableProps {
    children?: React.ReactNode;
    onClickOutside: ClickHandler | null;
}

export function OutsideClickable({
    children,
    onClickOutside,
}: OutsideClickableProps): JSX.Element | null {
    const rootRef = useRef<HTMLDivElement>(null);

    // The document listener is registered once, but must always call the
    // current handler — as `this.props.onClickOutside` did when this was a
    // class. Holding it in a ref keeps the listener stable without capturing
    // the handler from the first render.
    const onClickOutsideRef = useRef(onClickOutside);
    useEffect(() => {
        onClickOutsideRef.current = onClickOutside;
    }, [onClickOutside]);

    useEffect(() => {
        const documentClickhandler = (e: Event): void => {
            const self = rootRef.current;
            const handler = onClickOutsideRef.current;
            if ((!self || !self.contains(e.target as Node)) && handler) {
                handler(e);
            }
        };
        document.addEventListener('click', documentClickhandler, true);
        return () =>
            document.removeEventListener('click', documentClickhandler, true);
    }, []);

    return (
        <div ref={rootRef} style={{ display: 'contents' }}>
            {children}
        </div>
    );
}
